import { useMemo, useCallback } from 'react';
import { useToast } from './use-toast';
import { ProductService } from '@/services/ProductService';
import { useProductStore } from '@/store/useProductStore';
import type { CropData, Product } from '@/types';

export function useProductWorkflow() {
  const { toast } = useToast();
  const productService = useMemo(() => new ProductService(), []);

  // Select individual values and setters for stable references
  const step = useProductStore((s) => s.step);
  const masterImage = useProductStore((s) => s.masterImage);
  const productTitle = useProductStore((s) => s.productTitle);
  const productCategory = useProductStore((s) => s.productCategory);
  const crops = useProductStore((s) => s.crops);
  const productionStatus = useProductStore((s) => s.productionStatus);
  const productionProgress = useProductStore((s) => s.productionProgress);
  const currentProduct = useProductStore((s) => s.currentProduct);

  const setStep = useProductStore((s) => s.setStep);
  const setMasterImage = useProductStore((s) => s.setMasterImage);
  const setProductTitle = useProductStore((s) => s.setProductTitle);
  const setProductCategory = useProductStore((s) => s.setProductCategory);
  const setCrops = useProductStore((s) => s.setCrops);
  const setProductionStatus = useProductStore((s) => s.setProductionStatus);
  const setProductionProgress = useProductStore((s) => s.setProductionProgress);
  const setCurrentProduct = useProductStore((s) => s.setCurrentProduct);
  const reset = useProductStore((s) => s.reset);

  const handleImageSelect = useCallback((file: File) => {
    setMasterImage(file);
  }, [setMasterImage]);

  const handleClearImage = useCallback(() => {
    setMasterImage(null);
    setProductTitle('');
    setProductCategory('digital-art-print');
  }, [setMasterImage, setProductTitle, setProductCategory]);

  const handleStartCropping = useCallback(() => {
    if (!masterImage || !productTitle) {
      toast({
        title: 'Missing Information',
        description: 'Please provide image and title',
        variant: 'destructive',
      });
      return;
    }
    setStep('crop');
  }, [masterImage, productTitle, setStep, toast]);

  const handleCropsComplete = useCallback(async (completedCrops: CropData[]) => {
    setCrops(completedCrops);
    setStep('production');

    if (!masterImage || !productTitle) {
      toast({
        title: 'Missing Information',
        description: 'Master image and title are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setProductionStatus('CROPPING');
      setProductionProgress(10);

      setProductionStatus('UPLOADING');
      setProductionProgress(30);

      const product = await productService.createProduct(
        masterImage,
        completedCrops,
        productTitle,
        productCategory
      );

      setProductionStatus('SAVING');
      setProductionProgress(90);

      setProductionStatus('COMPLETED');
      setProductionProgress(100);
      setCurrentProduct(product);

      setStep('product');

      toast({
        title: 'Product Created!',
        description: 'Your product has been successfully created',
      });
    } catch (error) {
      setProductionStatus('ERROR');
      toast({
        title: 'Production Failed',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    }
  }, [
    masterImage,
    productTitle,
    productCategory,
    productService,
    setCrops,
    setStep,
    setProductionStatus,
    setProductionProgress,
    setCurrentProduct,
    toast,
  ]);

  const handleReset = useCallback(() => {
    reset();
    setStep('upload');
  }, [reset, setStep]);

  const navigateToProduct = useCallback((product: Product) => {
    setCurrentProduct(product);
    setStep('product');
  }, [setCurrentProduct, setStep]);

  const loadProductById = useCallback(async (id: string) => {
    try {
      const product = await productService.getProduct(id);
      if (product) {
        setCurrentProduct(product);
      }
    } catch (error) {
      console.error('Failed to load product:', error);
    }
  }, [productService, setCurrentProduct]);

  return {
    step,
    setStep,
    masterImage,
    productTitle,
    setProductTitle,
    productCategory,
    setProductCategory,
    crops,
    productionStatus,
    productionProgress,
    currentProduct,
    setCurrentProduct,

    handleImageSelect,
    handleClearImage,
    handleStartCropping,
    handleCropsComplete,
    handleReset,
    navigateToProduct,
    loadProductById,
  };
}
